import {
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  JSX,
  Listen,
  Method,
  State,
} from "@stencil/core";
import {
  DrawerStatusTypes,
  DrawerStatusValues,
} from "@phn/utility/constants/app";
import {
  registerContextualDrawerClosing,
  registerContextualDrawerOpening,
} from "./contextual-drawer-animation-sequence";
import animationManager from "../../../../shared/services/animation-manager";

@Component({
  tag: "phn-nd-contextual-drawer",
  styleUrl: "contextual-drawer.scss",
  scoped: true,
})
export class ContextualDrawer {
  @Event() openBackdrop!: EventEmitter<boolean>;

  @State() isDrawerOpened = false;

  @Event({ composed: true, bubbles: true })
  phnContextualDrawerStatus!: EventEmitter<{
    status: DrawerStatusTypes;
  }>;

  @Element() el!: HTMLPhnNdContextualDrawerElement;

  @Listen("phnCloseContextualDrawer", { target: "window" })
  closeContextualDrawer(): void {
    this.closeDrawer();
  }

  @Listen("phnOpenContextualDrawer", { target: "window" })
  openContextualDrawer(): void {
    this.openDrawer();
  }

  @Listen("keydown", { target: "window", passive: true })
  handleKeyDown(e: KeyboardEvent): void {
    if (e.key === "Escape" && this.isDrawerOpened) {
      this.closeDrawer();
      return;
    }
  }

  @Method()
  openDrawer() {
    animationManager.play("openContextualDrawer");
  }

  @Method()
  closeDrawer() {
    animationManager.play("closeContextualDrawer").then(() => {
      this.phnContextualDrawerStatus.emit({
        status: DrawerStatusValues.finishClosing,
      });
      this.isDrawerOpened = false;
    });
  }

  componentDidLoad(): void {
    registerContextualDrawerOpening({
      onStartOpening: () => {
        this.phnContextualDrawerStatus.emit({
          status: DrawerStatusValues.startOpening,
        });
        this.isDrawerOpened = true;
        const contextualDrawerHeader = document.getElementById(
          "contextual-drawer-header",
        );
        if (contextualDrawerHeader) {
          contextualDrawerHeader.style.display = "";
        }
      },
      onFinishOpening: () => {
        this.phnContextualDrawerStatus.emit({
          status: DrawerStatusValues.finishOpening,
        });
      },
    });

    registerContextualDrawerClosing({
      onStartClosing: () => {
        this.phnContextualDrawerStatus.emit({
          status: DrawerStatusValues.startClosing,
        });
      },
    });
  }

  render(): JSX.Element {
    return (
      <Host style={{ display: this.isDrawerOpened ? "" : "none" }}>
        <div class="phn-nd-contextual-drawer">
          <phn-p-button-pure
            class="close-button"
            theme="dark"
            hide-label="true"
            style={{ padding: "0.75rem" }}
            icon={"close"}
            size={"medium"}
            aria="{ 'aria-label': 'close modal dialog' }"
            data-test-id="close-button"
            onClick={() => {
              this.closeDrawer();
            }}
          />
          <div class="contextual-drawer" id="contextual-drawer">
            <div slot="contextual_drawer_header">
              <slot name="contextual_drawer_header"></slot>
            </div>
          </div>
          <div
            aria-hidden="true"
            class="contextual-drawer-backdrop"
            onClick={() => {
              this.closeDrawer();
            }}
          />
        </div>
      </Host>
    );
  }
}
